/**
 * Estados de EE.UU.: códigos válidos y cómo normalizar lo que sea que haya
 * escrito alguien en una casilla de "Estado".
 *
 * De dónde sale (Javier Volcanes, 2026-08-28): *"cuando quiere filtrar por
 * estado solo le sale TX"*. El desplegable de Estado en Clientes se arma con
 * los valores DISTINTOS de la columna, comparando texto con texto. Eso tiene
 * dos consecuencias feas: una ficha a la que la importación no le trajo el
 * estado no aparece en ninguna opción (y de ahí sale `estadoDesdeZip`, al
 * final de este archivo), y una escrita "fl" o "Florida" se cuenta aparte de
 * "FL" — tres opciones para un solo estado.
 *
 * Módulo PLANO a propósito (`.js`, sin JSX): lo importan tanto componentes
 * como `deskte-full.jsx`, y un `.jsx` aquí tumbaría el build (regla 48).
 *
 * OJO, hay dos listas más de estados en el repo y NO se tocan desde aquí
 * porque tienen otra forma y otro uso:
 *   - `components/ReportsClientsView.jsx` → pares [código, nombre] para su
 *     desplegable de informes.
 *   - `components/CompanyAppointmentsView.jsx` → nombres largos para la
 *     matriz de nombramientos por aseguradora.
 * Si algún día hay que unificarlas, éste es el sitio donde ponerlas.
 */

/**
 * Códigos postales válidos: 50 estados + DC + territorios + las tres
 * direcciones militares (AA/AE/AP), que son códigos legítimos y aparecen en
 * carteras con clientes desplegados.
 */
/** Los 50 estados + DC, en el orden de los desplegables (LicenseForm, cotizador). */
export const STATES_US = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP', 'AA', 'AE', 'AP',
]);

/**
 * Nombre largo -> código. Las claves van en minúsculas, sin acentos y sin
 * puntuación porque así se comparan (ver `plegar`). Incluye los nombres en
 * español que de verdad se teclean en estas carteras: una agencia
 * hispanohablante escribe "Nueva York" o "Carolina del Norte", y esa ficha no
 * puede quedarse fuera del filtro por eso.
 *
 * Es el mismo mapa que `deskte-api/src/utils/addressParts.ts`. Están duplicados
 * porque frontend y backend se despliegan por separado y no comparten módulos;
 * si se toca uno, se toca el otro.
 */
const NOMBRE_A_CODIGO = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK',
  oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI',
  wyoming: 'WY',
  'district of columbia': 'DC', 'washington dc': 'DC', 'washington d c': 'DC',
  'puerto rico': 'PR', 'virgin islands': 'VI', 'us virgin islands': 'VI',
  guam: 'GU', 'american samoa': 'AS', 'northern mariana islands': 'MP',
  'nueva york': 'NY', 'nueva jersey': 'NJ', 'nuevo mexico': 'NM',
  'carolina del norte': 'NC', 'carolina del sur': 'SC',
  'dakota del norte': 'ND', 'dakota del sur': 'SD',
  'nuevo hampshire': 'NH', 'virginia occidental': 'WV',
  'islas virgenes': 'VI',
};

/** minúsculas, sin acentos y sin puntuación, para comparar nombres largos. */
const plegar = (s) => String(s || '')
  .normalize('NFD')
  .replace(/\p{M}/gu, '')
  .toLowerCase()
  .replace(/[.,]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/**
 * Lo que sea que traiga la casilla -> código postal de dos letras.
 *
 * Devuelve `''` cuando NO lo reconoce. Ese vacío es información: el llamante
 * decide si se queda con el texto original (que es lo que hace el filtro de
 * Clientes, para no esconder una ficha por no saber leerla) o lo descarta.
 * Aquí nunca se inventa un código.
 */
export function normalizarEstado(bruto) {
  const s = String(bruto || '').trim();
  if (!s) return '';
  const may = s.toUpperCase().replace(/[.\s]/g, '');
  if (may.length === 2 && US_STATE_CODES.has(may)) return may;
  return NOMBRE_A_CODIGO[plegar(s)] || '';
}

/**
 * Clave con la que se AGRUPAN dos escrituras del mismo estado.
 *
 * "fl", "FL" y "Florida" comparten clave; un texto que no se reconoce se
 * agrupa por sí mismo en mayúsculas, así que sigue saliendo en el filtro en
 * vez de desaparecer. **Nunca devuelve vacío si entró algo**: una opción de
 * más se ve y se corrige; una ficha escondida no.
 */
export function claveEstado(bruto) {
  const s = String(bruto || '').trim();
  if (!s) return '';
  return normalizarEstado(s) || s.toUpperCase();
}

/**
 * Valor centinela de la opción "Sin estado" del filtro de Clientes.
 *
 * Se escribe con letras a propósito, sin caracteres raros: un centinela con un
 * byte NUL dentro convirtió un `.jsx` en binario para `git` y `grep`
 * (2026-08-13, `SheetsView.jsx`). Y no puede colisionar con un estado real
 * porque ningún código postal lleva guiones bajos.
 */
export const SIN_ESTADO = '__sin_estado__';

/**
 * Rangos de código postal -> estado, por los TRES primeros dígitos (el SCF de
 * USPS, que se asigna por estado). Es la tabla estándar.
 *
 * Es la misma que `deskte-api/src/utils/zipEstado.ts`, duplicada porque
 * frontend y backend se despliegan por separado: si se toca una, se toca la
 * otra. Ahí está explicado por qué las anomalías van a mano (733 Austin en el
 * bloque de Oklahoma, 885 El Paso en el de Nuevo México, 569 correo del
 * gobierno, 340 y 962-966 militares) y por qué **969 se queda fuera**: cubre
 * Guam, Marianas, Palaos, Micronesia y Marshall a la vez, y un prefijo que no
 * distingue no se adivina.
 */
const RANGOS_ZIP = [
  [5, 5, 'NY'], [6, 7, 'PR'], [8, 8, 'VI'], [9, 9, 'PR'],
  [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'],
  [50, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'], [90, 98, 'AE'],
  [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'], [200, 205, 'DC'],
  [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'],
  [290, 299, 'SC'], [300, 319, 'GA'], [320, 339, 'FL'], [340, 340, 'AA'],
  [341, 349, 'FL'], [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'],
  [398, 399, 'GA'], [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'],
  [480, 499, 'MI'], [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'],
  [569, 569, 'DC'], [570, 577, 'SD'], [580, 588, 'ND'], [590, 599, 'MT'],
  [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'], [680, 693, 'NE'],
  [700, 714, 'LA'], [716, 729, 'AR'], [730, 732, 'OK'], [733, 733, 'TX'],
  [734, 749, 'OK'], [750, 799, 'TX'], [800, 816, 'CO'], [820, 831, 'WY'],
  [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'],
  [885, 885, 'TX'], [889, 898, 'NV'], [900, 961, 'CA'], [962, 966, 'AP'],
  [967, 968, 'HI'], [970, 979, 'OR'], [980, 994, 'WA'], [995, 999, 'AK'],
];

/**
 * Estado al que pertenece un código postal. Devuelve `''` cuando el ZIP no es
 * de EE.UU., está mal escrito, o su prefijo no identifica un solo estado.
 *
 * Existe para no salir a internet: la ficha pregunta a zippopotam.us, que es
 * una llamada de red por cliente. Importando 1.400 fichas eso son 1.400
 * llamadas a un servicio gratuito ajeno.
 */
export function estadoDesdeZip(bruto) {
  const m = String(bruto || '').trim().match(/^(\d{5})(?:-\d{4})?$/);
  if (!m) return '';
  const zip3 = Number(m[1].slice(0, 3));
  for (const [desde, hasta, estado] of RANGOS_ZIP) {
    if (zip3 >= desde && zip3 <= hasta) return estado;
  }
  return '';
}
